import React from 'react';
import { changeRows, CONSEQUENCE_LABEL } from '../../lib/briefDiff';
import { fmtDate, fmtDateTime } from '../../lib/dates';
import { formatGBP } from '../../lib/money';

const FONT = "font-['Metropolis']";

// "What's changed since you responded" — the supplier's changes view
// (review-brief-amendments §3).
//
// The job: a supplier who already priced this brief needs to know, in one
// screen, whether the changes affect their number. So the panel leads with the
// changes themselves, most consequential first, and states the two things they
// then have to decide between — leave the quote, or update it.
//
// a11y (§6): "was" and "now" are TEXT labels, not a strikethrough alone —
// struck-through text is announced identically to normal text by most screen
// readers, so a diff conveyed only by <s> is invisible to them. The strike is
// decoration on top of the words.

const CONSEQUENCE_STYLE = {
  price:    'text-[#7A1B05] bg-[#E0290A]/[0.09] border-[#E0290A]/30',
  schedule: 'text-[#7A4E10] bg-[#E89A2E]/[0.12] border-[#E89A2E]/35',
};

// Values arrive as raw column values, so they need the same formatting the
// brief itself uses — an ISO string or a bare number in a change list reads as
// a different kind of thing from the brief it's describing.
const showValue = (row) => {
  const { type } = row;
  const fmt = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (type === 'currency') return formatGBP(v);
    if (type === 'date') return fmtDate(v);
    if (type === 'datetime') return fmtDateTime(v);
    if (type === 'bool') return v ? 'Yes' : 'No';
    return String(v);
  };
  return { was: fmt(row.was), now: fmt(row.now) };
};

export default function BriefChangesPanel({
  diff, amendedAt, versionNumber, note, savedQuote, onUpdateResponse, onReadFullBrief,
}) {
  const rows = changeRows(diff);
  if (rows.length === 0) return null;

  return (
    <section
      aria-labelledby="brief-changes-heading"
      className="bg-white border border-[#E89A2E]/40 rounded-2xl overflow-hidden mb-5">
      <div className="bg-[#E89A2E]/[0.10] px-5 py-4 border-b border-[#E89A2E]/30">
        <h2 id="brief-changes-heading" className={`${FONT} text-[15px] font-bold text-ink`}>
          What's changed since you responded
        </h2>
        <p className={`${FONT} text-[12px] text-[#7A4E10] mt-0.5`}>
          {rows.length} change{rows.length === 1 ? '' : 's'}
          {versionNumber ? ` · version ${versionNumber}` : ''}
          {amendedAt ? ` · ${fmtDateTime(amendedAt)}` : ''}
        </p>
        {/* The amender's own note, when they left one. It's the only place the
            reason for a change is stated in their words. */}
        {note && (
          <p className={`${FONT} text-[13px] text-[#4C4E42] mt-2 leading-snug whitespace-pre-wrap bg-white border border-[#E89A2E]/25 rounded-lg px-3 py-2`}>
            {note}
          </p>
        )}
      </div>

      <ol className="divide-y divide-[#F0F2E8]">
        {rows.map((row, i) => {
          const { was, now } = row.kind === 'field' ? showValue(row) : { was: null, now: null };
          return (
            <li key={`${row.kind}-${row.label}-${i}`} className="px-5 py-3.5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <p className={`${FONT} text-[13px] font-bold text-ink`}>
                  {row.kind === 'document' ? `${row.label}: ${row.name || 'Document'}` : row.label}
                </p>
                {row.consequence && (
                  <span className={`${FONT} text-[10px] font-bold uppercase tracking-[0.1em] rounded-full px-2.5 py-1 border flex-shrink-0 ${CONSEQUENCE_STYLE[row.consequence]}`}>
                    {CONSEQUENCE_LABEL[row.consequence]}
                  </span>
                )}
              </div>

              {row.kind === 'field' && (
                <div className={`${FONT} text-[13px] mt-1.5 space-y-0.5`}>
                  {/* "was"/"now" carry the meaning; the strike is decoration. */}
                  <p className="text-[#51534A]">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] mr-1.5">was</span>
                    <span className="line-through">{was ?? 'not set'}</span>
                  </p>
                  <p className="text-ink font-semibold">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] mr-1.5 text-fusion-forest">now</span>
                    {now ?? 'not set'}
                  </p>
                </div>
              )}

              {row.kind === 'body' && (
                <p className={`${FONT} text-[12px] text-[#51534A] mt-1`}>
                  The brief text has been edited. Read it in full below — the changes aren't only in the summary.
                </p>
              )}
            </li>
          );
        })}
      </ol>

      <div className="px-5 py-4 border-t border-[#F0F2E8] bg-[#F7F9F0]">
        {/* Naming their saved quote is the point of the footer: the decision is
            "does that number still stand?", so the number has to be in front of
            them while they decide. */}
        {savedQuote != null && savedQuote !== '' && (
          <p className={`${FONT} text-[13px] text-ink mb-3`}>
            Your saved quote is <strong>{formatGBP(savedQuote)}</strong> ex-VAT. It stands unless you change it.
          </p>
        )}
        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={onUpdateResponse}
            className={`${FONT} text-[13px] font-bold rounded-full px-5 py-2.5 text-white bg-[#2F6E14] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest`}>
            Update my response
          </button>
          <button
            type="button"
            onClick={onReadFullBrief}
            className={`${FONT} text-[13px] font-semibold rounded-full px-5 py-2.5 border border-[#2F6E14]/40 text-[#2F6E14] hover:bg-[#2F6E14]/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest`}>
            Read the full brief
          </button>
        </div>
      </div>
    </section>
  );
}
